import { getDefaultStore } from 'jotai';
import { chatSidePanelOpenAtom } from '@/features/chat/store';
import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { useTranslations } from 'use-intl';
import z from 'zod';
import {
  scopeAgentStatusQuery,
  scopeBreakdownQuery,
  scopeDescriptionQuery,
  scopePackageConditionsQuery,
  scopeStandardSetupQuery,
  scopePackageDocumentsQuery,
  scopePackageGenerationQuery,
  scopeProofreadQuery,
  scopeClientChangesQuery,
  scopeLinesQuery,
} from '@/features/scope-breakdown/queries';
import { workspaceQuery } from '@/features/workspaces/queries';
import { sheetSearchSchema } from '@/features/sheets/constants';
import { ScopeBreakdownPage } from '@/features/scope-breakdown/components/scope-breakdown-page';
import { artifactQuery } from '@/features/artifacts/queries';
import { sheetQuery } from '@/features/sheets/queries';
import { shownSheetId } from '@/features/scope-breakdown/utils';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/(main)/scope-breakdown/',
)({
  staticData: { chatSurface: 'kg_work_package' },
  onEnter: () => getDefaultStore().set(chatSidePanelOpenAtom, false),
  component: RouteComponent,
  loaderDeps: ({ search }) => ({
    sheetId: search.sheetId,
    sheetRevision: search.sheetRevision,
    scopePackage: search.scopePackage,
    scopeView: search.scopeView,
  }),
  loader: async ({ context, params, deps }) => {
    const workspace = await context.queryClient.query({
      ...workspaceQuery(params.workspaceId),
      staleTime: 'static',
    });
    const status = await context.queryClient.query({
      ...scopeAgentStatusQuery(params.workspaceId, workspace.type),
      staleTime: 'static',
    });
    const breakdown = deps.sheetId
      ? null
      : await context.queryClient.query({
          ...scopeBreakdownQuery(params.workspaceId),
          staleTime: 'static',
        });
    if (breakdown)
      await context.queryClient.query({
        ...scopeStandardSetupQuery(params.workspaceId),
        staleTime: 'static',
      });
    if (breakdown?.breakdown.startMode === 'client_posts')
      await context.queryClient.query({
        ...scopeLinesQuery(params.workspaceId),
        staleTime: 'static',
      });
    if (breakdown && deps.scopePackage) {
      const view =
        deps.scopeView ??
        (breakdown.breakdown.startMode === 'client_posts' ? 'client-posts' : 'requirements');
      const loadsEditor =
        ['work-pack', 'add-information', 'client-posts'].includes(view) ||
        (view === 'requirements' && breakdown.breakdown.startMode === 'client_posts');
      const pending: Promise<unknown>[] = [
        context.queryClient.query({
          ...scopePackageGenerationQuery(params.workspaceId, deps.scopePackage),
          staleTime: 'static',
        }),
        context.queryClient.query({
          ...scopePackageDocumentsQuery(params.workspaceId, deps.scopePackage),
          staleTime: 'static',
        }),
      ];
      if (
        loadsEditor ||
        ['choose-description', 'documents', 'generate', 'proofread'].includes(view)
      )
        pending.push(
          context.queryClient.query({
            ...scopeDescriptionQuery(params.workspaceId, deps.scopePackage),
            staleTime: 'static',
          }),
        );
      if (loadsEditor)
        pending.push(
          context.queryClient.query({
            ...scopePackageConditionsQuery(params.workspaceId, deps.scopePackage),
            staleTime: 'static',
          }),
        );
      if (view === 'proofread')
        pending.push(
          context.queryClient.query({
            ...scopeProofreadQuery(params.workspaceId, deps.scopePackage),
            staleTime: 'static',
          }),
        );
      if (view === 'changes' || view === 'generate')
        pending.push(
          context.queryClient.query({
            ...scopeClientChangesQuery(params.workspaceId, deps.scopePackage),
            staleTime: 'static',
          }),
        );
      await Promise.all(pending);
    }
    const sheetId = deps.sheetId ?? (breakdown ? null : shownSheetId(status));
    if (sheetId) {
      try {
        const artifact = await context.queryClient.query({
          ...artifactQuery(sheetId),
          staleTime: 'static',
        });
        if (artifact.kind !== 'sheet' || artifact.workspaceId !== params.workspaceId)
          throw notFound();
        if (deps.sheetRevision !== undefined)
          await context.queryClient.query({
            ...sheetQuery(sheetId, deps.sheetRevision),
            staleTime: 'static',
          });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) throw notFound();
        throw error;
      }
    }
    return status;
  },
  validateSearch: sheetSearchSchema.extend({
    scopePackage: z.string().optional(),
    scopeLine: z.string().optional(),
    scopeItem: z.string().optional(),
    scopeView: z
      .enum([
        'requirements',
        'client-posts',
        'changes',
        'choose-description',
        'work-pack',
        'add-information',
        'documents',
        'generate',
        'proofread',
        'send',
        'offers',
      ])
      .optional(),
  }),
});

function RouteComponent() {
  const tFeature = useTranslations('features.scopeBreakdown');
  const { workspaceId, projectId } = Route.useParams();

  return (
    <>
      <Header className="md:hidden">
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId"
          params={{ projectId, workspaceId }}
        />
        <WorkspaceBreadcrumbs>
          <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
        </WorkspaceBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ScopeBreakdownPage />
    </>
  );
}
